import { MenuItem, Select, Stack, TextField, Typography } from '@mui/material';
import { useState } from 'react';
import { conditionValues, type MultiSelectConditionComponentProps } from '@/types/advanceFilter';
import { ConditionPanelTriggerButton } from './conditionComponents';
import { ConditionPanelPopover } from './conditionPanelPopover';
import { FilterOperatorSelect } from './filterOperatorSelect';
import { SIZE_CONDITION_UNITS, toSizeDraft, toStoredSizeBytes } from './sizeConditionValue';
import { useConditionPanel } from './useConditionPanel';

/**
 * Editor for a property compared against a single numeric threshold, entered as
 * a size with a unit and stored as bytes.
 *
 * The property must declare `PropertyPolicyRelation.Numeric`, since the operator
 * carries the comparison and the value is one number rather than a set to pick
 * from.
 */
export const SizeCondition = (props: MultiSelectConditionComponentProps) => {
  const conditionLabel = props.property.condition?.displayName ?? props.property.displayName;
  const storedBytes = conditionValues(props.condition)[0];
  // The condition row remounts whenever its operator changes, so re-deriving the
  // draft here is what carries an already-entered threshold across that change.
  const [draft, setDraft] = useState(() => toSizeDraft(storedBytes));
  const panel = useConditionPanel(props.panelHost);
  const bytes = toStoredSizeBytes(draft.amount, draft.unit);
  const commit = (amount: string, unit: string) => {
    setDraft({
      amount,
      unit
    });
    const committed = toStoredSizeBytes(amount, unit);
    props.onChange({
      ...props.condition,
      value: committed === undefined ? [] : [String(committed)]
    });
  };
  return <>
      {!panel.isHosted && <ConditionPanelTriggerButton ref={panel.triggerRef} onClick={panel.toggle} options={storedBytes ? [{
      id: storedBytes,
      name: `${draft.amount} ${draft.unit}`
    }] : []} selectedValues={storedBytes ? [storedBytes] : []} />}
      <ConditionPanelPopover open={panel.isOpen} anchorEl={panel.anchorEl} onClose={panel.close}>
        <Stack className='w-[308px]' sx={{
        padding: '12px 24px',
        gap: '12px'
      }}>
          {panel.isHosted ? <FilterOperatorSelect condition={props.condition} property={props.property} onChange={props.onChange} /> : <Typography variant='body1'>
              {`Enter a ${conditionLabel.toLowerCase()} to compare against`}
            </Typography>}
          <Stack direction='row' sx={{
          gap: '12px',
          alignItems: 'center'
        }}>
            <TextField data-testid='size-condition-amount' className='w-[120px]' type='number' autoFocus error={draft.amount !== '' && bytes === undefined} value={draft.amount} onChange={event => commit(event.target.value, draft.unit)} slotProps={{
            htmlInput: {
              min: 0,
              step: 'any',
              inputMode: 'decimal'
            }
          }} sx={{
            '& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button': {
              display: 'none'
            },
            '& input[type=number]': {
              MozAppearance: 'textfield'
            }
          }} />
            <Select data-testid='size-condition-unit' className='w-[100px]' value={draft.unit} onChange={event_0 => commit(draft.amount, event_0.target.value)}>
              {SIZE_CONDITION_UNITS.map(unit_0 => <MenuItem key={unit_0} value={unit_0}>
                  {unit_0}
                </MenuItem>)}
            </Select>
          </Stack>
          {bytes !== undefined && <Typography variant='body2'>
              {`Saved as ${bytes.toLocaleString()} bytes.`}
            </Typography>}
        </Stack>
      </ConditionPanelPopover>
    </>;
};